import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { PAYER_CONFIG } from "../config/payer";
import { Claim } from "../types/claim";

interface EOBModalProps {
  claim: Claim;
  onClose: () => void;
}

const EOBModal = ({ claim, onClose }: EOBModalProps) => {
  const handlePrint = () => {
    const style = document.createElement("style");
    style.innerHTML = `
      @media print {
        body > * { display: none; }
        #printable-eob { display: block !important; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  const EOBContent = () => (
    <div className="p-6">
      {/* Patient and Provider Info */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h3 className="font-bold mb-2">Patient Information</h3>
          <p>Name: {claim.patient}</p>
          <p>Patient ID: {claim.patientId}</p>
          <p>Date of Service: {claim.date}</p>
          <p>Claim ID: {claim.id}</p>
        </div>
        <div>
          <h3 className="font-bold mb-2">Provider Information</h3>
          <p>Ivory Haven Dental</p>
          <p>123 Dental Plaza</p>
          <p>Healthcare City, HC 12345</p>
        </div>
      </div>

      {/* Service Details */}
      <div className="mt-6">
        <h3 className="font-bold mb-2">Service Details</h3>
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Service</th>
              <th className="p-2 text-left">Code</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-right">Billed Amount</th>
              <th className="p-2 text-right">Insurance Pays</th>
              <th className="p-2 text-right">Patient Pays</th>
            </tr>
          </thead>
          <tbody>
            {claim.services.map((service, index) => (
              <tr key={index} className="border-b">
                <td className="p-2">{service.procedure}</td>
                <td className="p-2 font-mono">{service.procedureCode}</td>
                <td className="p-2">{service.date}</td>
                <td className="p-2 text-right">
                  ${service.amount.toLocaleString()}
                </td>
                <td className="p-2 text-right">
                  ${(service.amount * 0.8).toLocaleString()}
                </td>
                <td className="p-2 text-right">
                  ${(service.amount * 0.2).toLocaleString()}
                </td>
              </tr>
            ))}
            <tr className="font-medium border-t">
              <td className="p-2" colSpan={3}>
                Totals
              </td>
              <td className="p-2 text-right">
                ${claim.totalAmount.toLocaleString()}
              </td>
              <td className="p-2 text-right">
                ${(claim.totalAmount * 0.8).toLocaleString()}
              </td>
              <td className="p-2 text-right">
                ${(claim.totalAmount * 0.2).toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="mt-6 bg-gray-50 p-4 rounded">
        <h3 className="font-bold mb-2">Claim Summary</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p>Status: {claim.status.desc}</p>
            <p>Payer: {PAYER_CONFIG.name}</p>
          </div>
          <div>
            <p>Processing Date: {new Date().toLocaleDateString()}</p>
            <p>
              Reference: EOB-{claim.id}-{new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      {/* Modal for screen */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full">
          <div className="p-4 border-b flex justify-between">
            <h2 className="text-xl font-bold">Explanation of Benefits</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full print:hidden"
            >
              <X />
            </button>
          </div>
          <EOBContent />
          <div className="p-4 border-t print:hidden">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Print EOB
            </button>
          </div>
        </div>
      </div>

      {/* Hidden div for printing */}
      <div id="printable-eob" className="hidden">
        <EOBContent />
      </div>
    </>,
    document.body
  );
};

export default EOBModal;
